/**
 * A coleta dos pórticos (`SC-03`, ADR-087): o modo de cada pórtico — o pórtico entrega, ou o
 * coletor busca — e, no modo busca, o último sinal de vida do coletor.
 *
 * O coletor fala com o cockpit **só pela API, com a chave de coletor de cada pórtico** — emitida
 * aqui, presa à praça, escopo `coleta`; a chave de campo do pórtico não alcança a coleta. A
 * credencial do fabricante mora com ele, nunca aqui, e ele só busca onde o arquivo dele diz para
 * cada praça. Por isso a tela pede só o fabricante, o endereço e o código do pórtico lá. Trocar
 * um dos três é apontar para outro pórtico: a retomada recomeça do zero (o que já entrou, a
 * ingestão reconhece e não cobra de novo), e a tela pede a confirmação antes. O pórtico cujo
 * contador voltou (a troca do controlador) para a coleta: recomeçar a retomada, com o motivo,
 * a destrava. A situação e o atraso vêm do servidor, pela regra do domínio — a tela não refaz a
 * conta.
 *
 * E as passagens que faltam (`SC-04`, ADR-088), nos dois modos: as lacunas abertas da sequência do
 * pórtico e quantas passagens faltam nelas, a lista com o intervalo, a hora e o desfecho
 * (recuperada ou perdida no pórtico), as recusadas de vez na coleta, e o aviso do número que chegou
 * repetido — o contador voltou, e recomeçar a retomada começa a numeração nova.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ChaveEmitida,
  ColetaDosPorticos as Coleta,
  ColetaNaTela,
  LacunasDoPortico,
  ModoDeColeta,
  SituacaoDaColeta,
  SituacaoDaLacuna,
} from "../api/tipos";
import { Badge, Botao, Cartao, Estado } from "../componentes/base";
import { Icone } from "../componentes/icones";
import type { Significado } from "../componentes/base";
import { dataEHora, numeroLongo } from "../formato";

const CAMINHO = "/tollvision/coleta/porticos";

const CAMPO =
  "min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]";

const SITUACAO: Record<SituacaoDaColeta, { rotulo: string; cor: Significado }> =
  {
    EM_DIA: { rotulo: "em dia", cor: "verde" },
    ATRASADA: { rotulo: "atrasada", cor: "amarelo" },
    PORTICO_SEM_RESPOSTA: { rotulo: "o pórtico não responde", cor: "vermelho" },
    SEM_SINAL: { rotulo: "o coletor não dá sinal", cor: "vermelho" },
  };

const DA_LACUNA: Record<SituacaoDaLacuna, Significado> = {
  ABERTA: "amarelo",
  RECUPERADA: "verde",
  PERDIDA: "vermelho",
};

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

function minutos(valor: number): string {
  if (valor < 60) return `${Math.floor(valor)} min`;
  const horas = Math.floor(valor / 60);
  return `${horas} h ${Math.floor(valor % 60)} min`;
}

export function ColetaDosPorticos() {
  const [coleta, definirColeta] = useState<Coleta | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      definirColeta(await api<Coleta>(CAMINHO));
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler a coleta."));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro) return <Estado tipo="erro" titulo={erro} />;
  if (!coleta)
    return <Estado tipo="carregando" titulo="Lendo a coleta dos pórticos…" />;
  if (coleta.porticos.length === 0)
    return (
      <Estado tipo="vazio" titulo="Nenhum pórtico ativo no seu recorte." />
    );

  return (
    <div className="grid gap-3">
      <p className="max-w-prose text-[11.5px] text-tinta-3">
        No modo <b>o pórtico entrega</b>, o pórtico chama o cockpit com a chave
        dele, como sempre. No modo <b>o coletor busca</b>, o coletor da
        concessionária pergunta ao pórtico o que veio depois da última passagem
        entregue e a entrega com a <b>chave de coletor</b> daquele pórtico,
        emitida aqui — a credencial do fabricante fica com ele, e ele só busca
        onde o arquivo dele diz para cada praça. A coleta que não alcança o
        pórtico há {coleta.aviso_min} minutos alerta, e é crítica em{" "}
        {coleta.critico_min / 60} horas.
      </p>
      {coleta.porticos.map((p) => (
        <Portico
          key={p.praca_id}
          portico={p}
          coleta={coleta}
          aoMudar={carregar}
        />
      ))}
    </div>
  );
}

function Portico({
  portico,
  coleta,
  aoMudar,
}: {
  portico: ColetaNaTela;
  coleta: Coleta;
  aoMudar: () => Promise<void>;
}) {
  const [modo, definirModo] = useState<ModoDeColeta>(portico.modo);
  const [fabricante, definirFabricante] = useState(
    portico.fabricante ?? coleta.fabricantes[0]?.codigo ?? "",
  );
  const [endereco, definirEndereco] = useState(portico.endereco ?? "");
  const [codigo, definirCodigo] = useState(portico.codigo_no_fabricante ?? "");
  const [aviso, definirAviso] = useState<string | null>(null);
  const [gravando, definirGravando] = useState(false);
  const [emitida, definirEmitida] = useState<ChaveEmitida | null>(null);

  useEffect(() => {
    definirModo(portico.modo);
    definirEndereco(portico.endereco ?? "");
    definirCodigo(portico.codigo_no_fabricante ?? "");
  }, [portico.modo, portico.endereco, portico.codigo_no_fabricante]);

  const busca = modo === "BUSCA";
  const outroPortico =
    portico.endereco !== null &&
    busca &&
    (endereco.trim() !== portico.endereco ||
      codigo.trim() !== (portico.codigo_no_fabricante ?? "") ||
      fabricante !== (portico.fabricante ?? ""));

  async function gravar() {
    if (
      outroPortico &&
      !window.confirm(
        `Apontar ${portico.praca_codigo} para outro pórtico? A retomada recomeça do zero: o coletor busca tudo o que o pórtico guarda, e o que já entrou o cockpit reconhece e não cobra de novo.`,
      )
    )
      return;
    definirGravando(true);
    try {
      await api<ColetaNaTela>(`${CAMINHO}/${portico.praca_id}`, {
        method: "PUT",
        corpo: busca
          ? {
              modo,
              fabricante,
              endereco: endereco.trim(),
              codigo_no_fabricante: codigo.trim(),
            }
          : { modo },
      });
      definirAviso("Coleta gravada.");
      await aoMudar();
    } catch (falha) {
      definirAviso(mensagem(falha, "Não foi possível gravar a coleta."));
    } finally {
      definirGravando(false);
    }
  }

  // A chave de coletor deste pórtico: escopo `coleta`, presa à praça (ADR-010). O segredo sai
  // uma vez, e vai para o arquivo do coletor — nunca para o pórtico.
  async function emitirChave() {
    try {
      definirEmitida(
        await api<ChaveEmitida>("/admin/chaves", {
          method: "POST",
          corpo: {
            nome: `Coletor · ${portico.praca_codigo}`,
            finalidade: "coletor",
            concessao_id: portico.concessao_id,
            praca_id: portico.praca_id,
          },
        }),
      );
      definirAviso(null);
    } catch (falha) {
      definirAviso(mensagem(falha, "Não foi possível emitir a chave."));
    }
  }

  async function recomecar() {
    const motivo = window.prompt(
      `Por que recomeçar a retomada de ${portico.praca_codigo}? O coletor busca de novo tudo o que o pórtico guarda (o que já entrou, o cockpit reconhece e não cobra de novo).`,
    );
    if (!motivo?.trim()) return;
    try {
      await api<ColetaNaTela>(`${CAMINHO}/${portico.praca_id}/recomecar`, {
        method: "POST",
        corpo: { motivo: motivo.trim() },
      });
      definirAviso(
        "Retomada recomeçada: o coletor busca do começo do buffer do pórtico.",
      );
      await aoMudar();
    } catch (falha) {
      definirAviso(mensagem(falha, "Não foi possível recomeçar a retomada."));
    }
  }

  const situacao = portico.situacao ? SITUACAO[portico.situacao] : null;
  return (
    <section
      aria-label={`Coleta de ${portico.praca_codigo}`}
      className="grid gap-2 rounded-cartao border border-linha bg-painel p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-bold text-tinta">
          {portico.praca_codigo} · {portico.praca_nome}
        </h3>
        <span className="text-[11px] text-tinta-3">
          {portico.concessao_codigo}
        </span>
        <Badge cor={portico.modo === "BUSCA" ? "azul" : "cinza"}>
          {portico.modo === "BUSCA" ? "o coletor busca" : "o pórtico entrega"}
        </Badge>
        {situacao && <Badge cor={situacao.cor}>{situacao.rotulo}</Badge>}
        {portico.gravidade && portico.atraso_min !== null && (
          <span
            className={`text-[11.5px] font-semibold ${portico.gravidade === "CRITICA" ? "text-vermelho" : "text-amarelo"}`}
          >
            sem alcançar o pórtico há {minutos(portico.atraso_min)}
          </span>
        )}
      </div>

      {portico.modo === "BUSCA" && <Sinal portico={portico} />}
      <Sequencia portico={portico} />

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gravar();
        }}
      >
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Modo
          <select
            value={modo}
            onChange={(e) => definirModo(e.target.value as ModoDeColeta)}
            className={CAMPO}
          >
            {coleta.modos.map((m) => (
              <option key={m.codigo} value={m.codigo}>
                {m.rotulo}
              </option>
            ))}
          </select>
        </label>
        {busca && (
          <>
            <label className="grid gap-1 text-[11px] text-tinta-3">
              Fabricante
              <select
                value={fabricante}
                onChange={(e) => definirFabricante(e.target.value)}
                className={CAMPO}
              >
                {coleta.fabricantes.map((f) => (
                  <option key={f.codigo} value={f.codigo}>
                    {f.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid min-w-[240px] flex-1 gap-1 text-[11px] text-tinta-3">
              Endereço do pórtico ou da estação
              <input
                value={endereco}
                onChange={(e) => definirEndereco(e.target.value)}
                placeholder="http://estacao-n2.concessionaria.local:8080"
                maxLength={300}
                className={`mono ${CAMPO}`}
              />
            </label>
            <label className="grid gap-1 text-[11px] text-tinta-3">
              Código no fabricante
              <input
                value={codigo}
                onChange={(e) => definirCodigo(e.target.value)}
                maxLength={64}
                className={`mono w-32 ${CAMPO}`}
              />
            </label>
          </>
        )}
        <Botao
          papel="primaria"
          type="submit"
          disabled={gravando || (busca && (!endereco.trim() || !codigo.trim()))}
        >
          Gravar
        </Botao>
        {portico.modo === "BUSCA" && (
          <Botao onClick={() => void emitirChave()}>
            <Icone.Chave />
            Chave do coletor
          </Botao>
        )}
        {(portico.modo === "BUSCA" || portico.repetidas > 0) && (
          <Botao onClick={() => void recomecar()}>Recomeçar a retomada</Botao>
        )}
      </form>
      {emitida && (
        <Cartao
          titulo="Guarde agora — o segredo não volta a aparecer"
          icone={<Icone.Alerta />}
        >
          <p className="mono rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2.5 py-2 text-[11.5px] wrap-anywhere text-amarelo">
            {emitida.segredo}
          </p>
          <p className="text-[10.5px] text-tinta-3">
            Vai no arquivo do coletor, junto da praça {portico.praca_codigo}:
            com ela o coletor pede a vez, entrega as passagens e manda o sinal
            deste pórtico, e de nenhum outro.
          </p>
          <Botao onClick={() => definirEmitida(null)}>Já guardei</Botao>
        </Cartao>
      )}
      {outroPortico && (
        <p className="text-[11px] text-amarelo">
          Outro fabricante, endereço ou código é outro pórtico: ao gravar, a
          retomada recomeça do zero.
        </p>
      )}
      {aviso && (
        <p role="status" className="text-[11.5px] text-tinta-2">
          {aviso}
        </p>
      )}
    </section>
  );
}

function Sinal({ portico }: { portico: ColetaNaTela }) {
  const linhas: [string, string][] = [
    [
      "Último sinal do coletor",
      portico.sinal_em
        ? `${dataEHora(portico.sinal_em)}${portico.vez_de ? ` · ${portico.vez_de}` : ""}`
        : "nenhum ainda",
    ],
    [
      "Entregue até",
      portico.ultima_no_portico !== null
        ? `${numeroLongo(portico.entregue_ate)} de ${numeroLongo(portico.ultima_no_portico)} · ${numeroLongo(portico.pendentes ?? 0)} esperando`
        : numeroLongo(portico.entregue_ate),
    ],
  ];
  if (portico.ocupacao !== null)
    linhas.push([
      "Buffer do pórtico",
      `${Math.round(portico.ocupacao * 100)}% ocupado`,
    ]);
  if (portico.desvio_s !== null && Math.abs(portico.desvio_s) >= 60)
    linhas.push([
      "Relógio do pórtico",
      `${portico.desvio_s > 0 ? "adiantado" : "atrasado"} ${minutos(Math.abs(portico.desvio_s) / 60)}`,
    ]);
  if (portico.perdidas > 0)
    linhas.push([
      "Perdidas no buffer",
      `${numeroLongo(portico.perdidas)}${portico.ultima_perda_em ? ` · a última em ${dataEHora(portico.ultima_perda_em)}` : ""}`,
    ]);
  if (portico.recusadas > 0)
    linhas.push([
      "Recusadas pelo cockpit",
      `${numeroLongo(portico.recusadas)}${portico.ultima_recusa ? ` · ${portico.ultima_recusa}` : ""}`,
    ]);
  return (
    <div className="grid gap-1">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-[11.5px] sm:grid-cols-[max-content_1fr]">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo} className="contents">
            <dt className="text-tinta-3">{rotulo}</dt>
            <dd className="text-tinta">{valor}</dd>
          </div>
        ))}
      </dl>
      {portico.problema && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          Na última rodada: {portico.problema}.
        </p>
      )}
    </div>
  );
}

/** As passagens que faltam na sequência do pórtico (`SC-04`): o resumo, o aviso do número
 * repetido e, a pedido, a lista das lacunas e das recusas. */
function Sequencia({ portico }: { portico: ColetaNaTela }) {
  const [lista, definirLista] = useState<LacunasDoPortico | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  async function abrir() {
    try {
      definirLista(
        await api<LacunasDoPortico>(`${CAMINHO}/${portico.praca_id}/lacunas`),
      );
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler as lacunas."));
    }
  }

  const rotulo = Object.fromEntries(
    (lista?.situacoes ?? []).map((s) => [s.codigo, s.rotulo]),
  );
  return (
    <div className="grid gap-1 text-[11.5px]">
      <div className="flex flex-wrap items-center gap-2">
        {portico.lacunas_abertas > 0 ? (
          <span className="font-semibold text-amarelo">
            Faltam {numeroLongo(portico.faltam)}{" "}
            {portico.faltam === 1 ? "passagem" : "passagens"}, em{" "}
            {numeroLongo(portico.lacunas_abertas)}{" "}
            {portico.lacunas_abertas === 1 ? "lacuna" : "lacunas"} da sequência
          </span>
        ) : (
          <span className="text-tinta-3">
            Nenhuma passagem faltando na sequência.
          </span>
        )}
        <Botao onClick={() => void (lista ? definirLista(null) : abrir())}>
          {lista ? "Fechar as lacunas" : "Ver as lacunas"}
        </Botao>
      </div>
      {portico.repetidas > 0 && (
        <p role="alert" className="text-amarelo">
          {numeroLongo(portico.repetidas)}{" "}
          {portico.repetidas === 1 ? "passagem chegou" : "passagens chegaram"}{" "}
          com um número que já era de outra
          {portico.ultima_repetida !== null &&
            ` (a última, a ${numeroLongo(portico.ultima_repetida)}${portico.repetida_em ? `, em ${dataEHora(portico.repetida_em)}` : ""})`}
          : entraram sem ele. O contador do pórtico voltou? Recomeçar a retomada
          começa a numeração nova.
        </p>
      )}
      {erro && <p className="text-vermelho">{erro}</p>}
      {lista && (
        <div className="grid gap-1.5">
          {lista.lacunas.length === 0 ? (
            <p className="text-tinta-3">Nenhuma lacuna até aqui.</p>
          ) : (
            <ul
              aria-label={`Lacunas de ${portico.praca_codigo}`}
              className="grid gap-1"
            >
              {lista.lacunas.map((l) => (
                <li
                  key={`${l.numeracao}-${l.de}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-1.5"
                >
                  <span className="mono text-tinta">
                    {l.de === l.ate
                      ? numeroLongo(l.de)
                      : `${numeroLongo(l.de)} a ${numeroLongo(l.ate)}`}
                  </span>
                  <Badge cor={DA_LACUNA[l.situacao]}>
                    {rotulo[l.situacao] ?? l.situacao}
                  </Badge>
                  <span className="text-tinta-3">
                    {l.depois_de || l.antes_de
                      ? `passaram ${l.depois_de ? `depois de ${dataEHora(l.depois_de)}` : ""}${l.depois_de && l.antes_de ? " e " : ""}${l.antes_de ? `antes de ${dataEHora(l.antes_de)}` : ""}`
                      : `vista em ${dataEHora(l.aberta_em)}`}
                  </span>
                  {l.fechada_em && (
                    <span className="text-tinta-3">
                      · {numeroLongo(l.recuperadas ?? 0)} chegaram,{" "}
                      {numeroLongo(l.recusadas ?? 0)} recusadas,{" "}
                      {numeroLongo(l.perdidas ?? 0)} perdidas
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {lista.recusas.length > 0 && (
            <ul
              aria-label={`Recusadas na coleta de ${portico.praca_codigo}`}
              className="grid gap-0.5"
            >
              {lista.recusas.map((r) => (
                <li
                  key={`${r.numeracao}-${r.sequencia}`}
                  className="text-tinta-2"
                >
                  <span className="mono">{numeroLongo(r.sequencia)}</span> ·{" "}
                  <span className="mono">{r.referencia}</span>: {r.motivo}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
